import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { inputCls } from "@/admin/ui";

const humanize = (k) => k.replace(/([A-Z])/g, " $1").replace(/[_-]/g, " ").replace(/^./, (c) => c.toUpperCase()).trim();

const blank = (sample) => {
  if (Array.isArray(sample)) return [];
  if (sample && typeof sample === "object") return Object.fromEntries(Object.entries(sample).map(([k, v]) => [k, blank(v)]));
  if (typeof sample === "number") return 0;
  if (typeof sample === "boolean") return false;
  return "";
};

const Text = ({ value, onChange, path }) => {
  const long = typeof value === "string" && value.length > 90;
  const cls = `${inputCls} py-2.5 text-sm`;
  return long ? (
    <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={Math.min(8, Math.ceil(value.length / 90) + 1)} className={`${cls} resize-y`} data-testid={`field-${path}`} />
  ) : (
    <input value={value} onChange={(e) => onChange(e.target.value)} className={cls} data-testid={`field-${path}`} />
  );
};

function Field({ value, onChange, path = "root", sample, depth = 0 }) {
  if (typeof value === "string") return <Text value={value} onChange={onChange} path={path} />;
  if (typeof value === "number") return <input type="number" value={value} onChange={(e) => onChange(Number(e.target.value))} className={`${inputCls} py-2.5 w-40`} data-testid={`field-${path}`} />;
  if (typeof value === "boolean") {
    return (
      <button type="button" onClick={() => onChange(!value)} data-testid={`field-${path}`} className={`inline-flex h-7 w-12 items-center rounded-full border transition-colors ${value ? "bg-amber-500 border-amber-500 justify-end" : "bg-slate-200 border-slate-300 justify-start"}`}>
        <span className="mx-0.5 h-5 w-5 rounded-full bg-white shadow" />
      </button>
    );
  }
  if (Array.isArray(value)) {
    const proto = value[0] ?? sample?.[0] ?? "";
    const primitive = typeof proto !== "object";
    const move = (i, d) => {
      const n = [...value];
      const [it] = n.splice(i, 1);
      n.splice(i + d, 0, it);
      onChange(n);
    };
    return (
      <div className="space-y-3" data-testid={`list-${path}`}>
        {value.map((item, i) => (
          <div key={i} className={`flex gap-2 ${primitive ? "items-center" : "items-start"}`}>
            <div className={`flex-1 ${primitive ? "" : "rounded-xl border border-slate-200 bg-slate-50/60 p-4"}`}>
              <FieldEditor value={item} sample={proto} path={`${path}-${i}`} depth={depth + 1} onChange={(v) => onChange(value.map((x, j) => (j === i ? v : x)))} />
            </div>
            <div className="flex flex-col gap-1 shrink-0">
              <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="p-1.5 text-slate-400 hover:text-[#0B1528] disabled:opacity-30" aria-label="Move up"><ArrowUp className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={() => move(i, 1)} disabled={i === value.length - 1} className="p-1.5 text-slate-400 hover:text-[#0B1528] disabled:opacity-30" aria-label="Move down"><ArrowDown className="h-3.5 w-3.5" /></button>
              <button type="button" onClick={() => onChange(value.filter((_, j) => j !== i))} className="p-1.5 text-slate-400 hover:text-red-600" aria-label="Remove" data-testid={`remove-${path}-${i}`}><Trash2 className="h-3.5 w-3.5" /></button>
            </div>
          </div>
        ))}
        <button type="button" onClick={() => onChange([...value, blank(proto)])} className="inline-flex items-center gap-1.5 rounded-full border border-dashed border-slate-300 px-3.5 py-1.5 text-xs font-semibold text-slate-600 hover:border-amber-500 hover:text-amber-600" data-testid={`add-${path}`}>
          <Plus className="h-3.5 w-3.5" /> Add item
        </button>
      </div>
    );
  }
  if (value && typeof value === "object") {
    return (
      <div className={`grid gap-4 ${depth > 0 ? "sm:grid-cols-2" : ""}`}>
        {Object.entries(value).map(([k, v]) => {
          const wide = typeof v !== "string" || v.length > 60 || Array.isArray(v);
          return (
            <div key={k} className={wide && depth > 0 ? "sm:col-span-2" : ""}>
              <label className="mb-1.5 block font-mono text-[10px] tracking-[0.2em] uppercase text-slate-500">{humanize(k)}</label>
              <FieldEditor value={v} sample={sample?.[k]} path={`${path}-${k}`} depth={depth + 1} onChange={(nv) => onChange({ ...value, [k]: nv })} />
            </div>
          );
        })}
      </div>
    );
  }
  return null;
}

export const FieldEditor = (props) => Field(props);
